"use client";

import classNames from "classnames/bind";
import { gsap } from "gsap";
import { useEffect, useRef } from "react";
import styles from "./AnimatedLogo.module.css";

const cx = classNames.bind(styles);

export const AnimatedLogo = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);

  useEffect(() => {
    const svg = svgRef.current;
    const paths = pathRefs.current.filter(
      (path): path is SVGPathElement => path !== null,
    );

    if (!svg || paths.length === 0) return;

    // Set initial transforms for paths
    paths.forEach((path) => {
      gsap.set(path, {
        transformOrigin: "center center",
        scale: 1,
      });
    });

    const handleMouseEnter = (e: MouseEvent) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      paths.forEach((path) => {
        // Get path center
        const pathRect = path.getBoundingClientRect();
        const pathCenterX = pathRect.left + pathRect.width / 2;
        const pathCenterY = pathRect.top + pathRect.height / 2;

        // Calculate direction from mouse to path center
        const deltaX = pathCenterX - mouseX;
        const deltaY = pathCenterY - mouseY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Normalize and scale the movement
        const maxDistance = 150; // Maximum distance for effect
        const effectStrength = Math.max(0, 1 - distance / maxDistance);
        const moveDistance = effectStrength * 15; // Maximum movement distance

        const normalizedX =
          distance > 0 ? (deltaX / distance) * moveDistance : 0;
        const normalizedY =
          distance > 0 ? (deltaY / distance) * moveDistance : 0;

        // Animate the path away from mouse
        gsap.to(path, {
          x: normalizedX,
          y: normalizedY,
          scale: 1 + effectStrength * 0.2,
          duration: 0.3,
          ease: "power2.out",
        });
      });
    };

    const handleMouseLeave = () => {
      paths.forEach((path) => {
        gsap.to(path, {
          x: 0,
          y: 0,
          scale: 1,
          duration: 0.5,
          ease: "elastic.out(1, 0.5)",
        });
      });
    };

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      const touchX = touch.clientX;
      const touchY = touch.clientY;

      paths.forEach((path) => {
        // Get path center
        const pathRect = path.getBoundingClientRect();
        const pathCenterX = pathRect.left + pathRect.width / 2;
        const pathCenterY = pathRect.top + pathRect.height / 2;

        // Calculate direction from touch to path center
        const deltaX = pathCenterX - touchX;
        const deltaY = pathCenterY - touchY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Normalize and scale the movement
        const maxDistance = 150;
        const effectStrength = Math.max(0, 1 - distance / maxDistance);
        const moveDistance = effectStrength * 20;

        const normalizedX =
          distance > 0 ? (deltaX / distance) * moveDistance : 0;
        const normalizedY =
          distance > 0 ? (deltaY / distance) * moveDistance : 0;

        // Animate with a spring back effect
        gsap
          .timeline()
          .to(path, {
            x: normalizedX,
            y: normalizedY,
            scale: 1 + effectStrength * 0.25,
            duration: 0.2,
            ease: "power2.out",
          })
          .to(path, {
            x: 0,
            y: 0,
            scale: 1,
            duration: 0.6,
            ease: "elastic.out(1, 0.4)",
          });
      });
    };

    // Add event listeners
    svg.addEventListener("mousemove", handleMouseEnter);
    svg.addEventListener("mouseleave", handleMouseLeave);
    svg.addEventListener("touchstart", handleTouchStart, { passive: false });

    return () => {
      svg.removeEventListener("mousemove", handleMouseEnter);
      svg.removeEventListener("mouseleave", handleMouseLeave);
      svg.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  return (
    <div className={cx("animated-logo")}>
      <svg
        ref={svgRef}
        width="297"
        height="295"
        viewBox="0 0 297 295"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cx("logo-svg")}
      >
        {/* Split the original path into 4 parts for individual animation */}
        {/* Top-right quarter */}
        <path
          ref={(el) => {
            pathRefs.current[0] = el;
          }}
          d="M154.639 141.402H297C294.078 64.7495 232.106 2.9028 154.639 0V141.402Z"
          fill="#16265B"
          className={cx("logo-path")}
        />
        {/* Top-left quarter */}
        <path
          ref={(el) => {
            pathRefs.current[1] = el;
          }}
          d="M0 141.402C78.6337 141.402 142.361 78.1042 142.361 0C64.8945 2.9028 2.92248 64.7495 0 141.402Z"
          fill="#16265B"
          className={cx("logo-path")}
        />
        {/* Bottom-right quarter */}
        <path
          ref={(el) => {
            pathRefs.current[2] = el;
          }}
          d="M154.639 295C231.811 292.097 294.078 230.542 297 153.598C216.902 153.598 154.639 216.896 154.639 295Z"
          fill="#16265B"
          className={cx("logo-path")}
        />
        {/* Bottom-left quarter */}
        <path
          ref={(el) => {
            pathRefs.current[3] = el;
          }}
          d="M142.361 295V153.598H0C2.92248 230.25 65.1885 292.097 142.361 295Z"
          fill="#16265B"
          className={cx("logo-path")}
        />
      </svg>
    </div>
  );
};
